
// Sentry — defer init based on page type
// Landing pages: delay 10s (visitors rarely trigger errors)
// Authenticated pages: load on idle (need error tracking sooner)
import { initSentry } from './services/sentry';
const isAuthPage = /^\/(dashboard|admin|settings|wallet|projects|billing|profile|login|register)/.test(window.location.pathname);
const isLocalDesignPreview = import.meta.env.DEV && window.location.pathname === '/landing-redesign-preview';
if (!isLocalDesignPreview) {
  if (isAuthPage) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => initSentry());
    } else {
      setTimeout(() => initSentry(), 2000);
    }
  } else {
    setTimeout(() => initSentry(), 10000);
  }
}

window.addEventListener('error', (e) => {
  const msg = e.message || '';
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed')) {
    if (!sessionStorage.getItem('reloaded_for_module_error')) {
      sessionStorage.setItem('reloaded_for_module_error', 'true');
      window.location.reload();
    }
  }
});

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import './src/main.css';
import './components/landing/TrafficLandingV2.css';
import './components/QuietOutlineForms.css';
import QuietOutlineSelectBridge from './components/QuietOutlineSelectBridge';
import { brandConfig } from './config/brand';
import { initializeTheme } from './config/theme';

initializeTheme();

const rootStyle = document.documentElement.style;
rootStyle.setProperty('--brand-primary', brandConfig.primaryColor);
rootStyle.setProperty('--brand-primary-hover', brandConfig.primaryHover);

// Firebase — only load on authenticated pages (694KB bundle)
// Landing pages never need Firebase, so skip entirely
if (isAuthPage) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => import('./services/firebase'));
  } else {
    setTimeout(() => import('./services/firebase'), 2000);
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// The static defaults keep first-response HTML useful before React loads.
// Remove them before Helmet mounts so it never adopts and later loses a tag
// that still carries our static `data-default-seo` marker.
document.head
  .querySelectorAll('[data-default-seo="true"]')
  .forEach((tag) => tag.remove());

declare global {
  interface Window {
    __TC_APP_BOOTED__?: boolean;
  }
}

if (!window.__TC_APP_BOOTED__) {
  window.__TC_APP_BOOTED__ = true;
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <HelmetProvider>
      <BrowserRouter>
        <QuietOutlineSelectBridge />
        <App />
      </BrowserRouter>
    </HelmetProvider>
  );
}
